import { Typography, Sheet, Tooltip } from "@mui/joy";
import { NavItemDisplay } from "./NavMenu";
import React from "react";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { SxProps } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { colors } from "./theme/tokens";

interface NavItemProps {
	item: NavItemDisplay;
	selected: boolean;
	collapsed?: boolean;
	currentPath?: string;
}

export default function NavItem(props: NavItemProps) {
	const [over, setOver] = React.useState(false);
	const [flyoutPos, setFlyoutPos] = React.useState({ top: 0, left: 0 });
	const itemRef = React.useRef<HTMLDivElement>(null);
	const collapsed = props.collapsed ?? false;
	const Icon = props.item.Icon;
	const textColor = colors.text.nav;

	const textSx: SxProps = {
		color: textColor,
		display: collapsed ? "none" : "block",
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	};

	const sheetSx: SxProps = {
		px: collapsed ? 0.5 : 1,
		py: 0.375,
		textAlign: collapsed ? "center" : "left",
		backgroundColor: props.selected ? colors.sidebar.active : colors.sidebar.background,
		cursor: "pointer",
		"&:hover": {
			backgroundColor: props.selected ? colors.sidebar.active : colors.sidebar.hover,
		},
		span: {
			float: "right",
		},
		position: "relative",
		borderRadius: 0,
		display: "flex",
		alignItems: "center",
		justifyContent: collapsed ? "center" : "flex-start",
		gap: collapsed ? 0 : 0.75,
		minHeight: 32,
	};
	const n = useNavigate();

	const onNav = (item: NavItemDisplay) => {
		if (item.ChildItems) {
			onNav(item.ChildItems[0]);
			return;
		}

		if (item.Route) {
			n(item.Route);
		}
	};

	const updateFlyoutPos = () => {
		if (itemRef.current) {
			const rect = itemRef.current.getBoundingClientRect();
			setFlyoutPos({ top: rect.top, left: rect.right });
		}
	};

	const content = (
		<Sheet
			ref={itemRef}
			sx={sheetSx}
			onMouseEnter={() => {
				setOver(true);
				updateFlyoutPos();
			}}
			onMouseLeave={() => setOver(false)}
			onClick={(event) => {
				event.preventDefault();
				event.stopPropagation();
				onNav(props.item);
			}}>
			{Icon && (
				<Icon
					sx={{
						fontSize: 18,
						color: textColor,
						flexShrink: 0,
					}}
				/>
			)}
			<Typography
				level="body-sm"
				fontWeight={props.selected ? 600 : 400}
				sx={{ ...textSx, flex: 1 }}
				endDecorator={
					!collapsed && props.item.ChildItems && props.item.ChildItems.length > 0 ? (
						<KeyboardArrowRightIcon sx={{ float: "right" }} style={{ float: "right" }} />
					) : (
						<></>
					)
				}>
				{props.item.Display}
			</Typography>
			{props.item.ChildItems && (
				<Sheet
					onMouseEnter={() => setOver(true)}
					onMouseLeave={() => setOver(false)}
					sx={{
						position: "fixed",
						width: "200px",
						visibility: over ? "visible" : "hidden",
						backgroundColor: colors.sidebar.background,
						border: `1px solid ${colors.border.default}`,
						borderRadius: 0,
						boxShadow: "md",
						top: flyoutPos.top,
						left: flyoutPos.left,
						display: "block",
						zIndex: 1200,
					}}>
					{props.item.ChildItems.map((x) => (
						<NavItem
							key={x.Display}
							item={x}
							selected={props.currentPath === (x.Route as string)}
							collapsed={false}
							currentPath={props.currentPath}
						/>
					))}
				</Sheet>
			)}
		</Sheet>
	);

	if (collapsed && !props.item.ChildItems) {
		return (
			<Tooltip title={props.item.Display} placement="right" arrow>
				{content}
			</Tooltip>
		);
	}

	return content;
}
